import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ChatWidget from './ChatWidget';
import api from '../services/api';

const VIEW_AS_ROLES = [
  { value: 'admin',   label: 'Admin' },
  { value: 'cad',     label: 'CAD' },
  { value: 'records', label: 'Records' },
  { value: 'gis',     label: 'Geospatial' },
  { value: 'upload',  label: 'Upload' },
];

export default function Layout({ children }) {
  const { user, realUser, viewAs, setViewAs, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);

  // Close the mobile menu whenever the route changes
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  // 811 Ticket Finder fail alert — red banner on every page for the roles that use
  // the tab, until a search succeeds again. Keyed to the real role (the API is).
  const alertRole = (realUser || user)?.role;
  const [ticketAlert, setTicketAlert] = useState(null);
  useEffect(() => {
    if (alertRole !== 'admin' && alertRole !== 'upload') { setTicketAlert(null); return; }
    let live = true;
    api.get('/tickets/alert')
      .then(({ data }) => { if (live) setTicketAlert(data.alert); })
      .catch(() => {});
    return () => { live = false; };
  }, [alertRole, location.pathname]);

  function handleLogout() {
    logout();
    navigate('/login');
  }

  // Admin "view as" — preview the app as another role. Go home on switch since
  // the current page may not exist for the viewed role.
  function handleViewAs(role) {
    setViewAs(role);
    navigate('/');
  }

  const navLinks = [
    ...(user?.role === 'upload'
      ? [
          { to: '/dashboard', label: 'CAD' },
          { to: '/records', label: 'Records' },
          { to: '/digs', label: 'Digs' },
          { to: '/tickets', label: '811 Ticket Finder' },
          { to: '/tools', label: 'Tools' },
        ]
      : user?.role === 'gis'
      ? [{ to: '/gis', label: 'Geospatial' }, { to: '/tools', label: 'Tools' }]
      : [
          { to: user?.role === 'records' ? '/records' : '/dashboard', label: user?.role === 'records' ? 'Records' : 'CAD' },
          { to: '/directory', label: 'Directory' },
          ...((user?.role === 'cad' || user?.role === 'admin') ? [{ to: '/qc', label: 'QC' }, { to: '/tools', label: 'Tools' }] : []),
          ...(user?.role === 'records' ? [{ to: '/digs', label: 'Digs' }, { to: '/contacts', label: '811 Contacts' }] : []),
          // Non-admin stats link — cad/records users get a personal "My Stats" view
          ...(user?.role === 'cad' || user?.role === 'records' ? [{ to: '/stats', label: 'My Stats' }] : []),
          ...(user?.role === 'admin' ? [{ to: '/records', label: 'Records' }, { to: '/digs', label: 'Digs' }, { to: '/contacts', label: '811 Contacts' }, { to: '/tickets', label: '811 Ticket Finder' }, { to: '/gis', label: 'Geospatial' }, { to: '/stats', label: 'Stats' }, { to: '/admin', label: 'Admin' }] : []),
        ]),
    ...(user?.role !== 'upload' ? [{ to: '/leave', label: 'Time Off' }, { to: '/break-room', label: 'Break Room' }] : []),
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{
      backgroundColor: '#0d1b2e',
      backgroundImage: "url('/topo-bg.svg'), radial-gradient(ellipse at top, #142236 0%, #0d1b2e 70%)",
      backgroundRepeat: 'no-repeat, no-repeat',
      backgroundSize: 'cover, cover',
      backgroundAttachment: 'fixed, fixed',
    }}>
      {/* Desktop nav (unchanged — md and up) */}
      <nav className="hidden md:flex border-b border-navy-600 px-6 py-3 items-center justify-between bg-navy-900" style={{ backgroundColor: '#080f1a' }}>
        <div className="flex items-center gap-8">
          <img src="/img/blvd-logo.png" alt="BLVD" className="h-6 w-auto" />
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`relative text-sm font-medium tracking-wide uppercase transition ${
                location.pathname === link.to
                  ? 'text-gold-500'
                  : 'text-gray-400 hover:text-gold-400'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-4">
          {realUser?.role === 'admin' ? (
            /* Admins: name is a menu — click to view the app as another role */
            <div className="relative">
              <button
                onClick={() => setViewMenuOpen((o) => !o)}
                className="text-sm text-gray-400 hover:text-gray-200 transition"
                title="View as another role"
              >
                {user?.name} &middot;{' '}
                <span className="uppercase text-xs font-bold text-gold-500">{user?.role}</span>
                <span className="text-gray-500 text-xs ml-1">▾</span>
              </button>
              {viewMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setViewMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 z-20 rounded-lg border shadow-xl overflow-hidden w-40"
                       style={{ backgroundColor: '#0d1b2e', borderColor: '#1e3a5f' }}>
                    <p className="px-3 pt-2 pb-1 text-[10px] font-bold text-gray-500 uppercase tracking-widest">View as</p>
                    {VIEW_AS_ROLES.map((r) => {
                      const active = (viewAs || 'admin') === r.value;
                      return (
                        <button
                          key={r.value}
                          onClick={() => { setViewMenuOpen(false); handleViewAs(r.value); }}
                          className="block w-full text-left px-3 py-2 text-xs uppercase tracking-wide font-bold transition hover:bg-white/5"
                          style={{ color: active ? '#c9a84c' : '#9ca3af' }}
                        >
                          {r.label}{active && <span className="ml-2">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          ) : (
            <span className="text-sm text-gray-400">
              {user?.name} &middot;{' '}
              <span className="uppercase text-xs font-bold text-gold-500">{user?.role}</span>
            </span>
          )}
          <button
            onClick={handleLogout}
            className="text-xs text-gray-500 hover:text-red-400 uppercase tracking-wide transition"
          >
            Sign out
          </button>
        </div>
      </nav>

      {/* Mobile nav (below md) */}
      <nav className="md:hidden border-b px-4 py-3" style={{ backgroundColor: '#080f1a', borderColor: '#1e3a5f' }}>
        <div className="flex items-center justify-between">
          <img src="/img/blvd-logo.png" alt="BLVD" className="h-6 w-auto" />
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="relative text-gray-200 hover:text-gold-400 transition text-2xl leading-none px-2"
            aria-label="Menu"
          >
            {menuOpen ? '✕' : '☰'}
          </button>
        </div>

        {menuOpen && (
          <div className="flex flex-col gap-0.5 mt-3 pt-3 border-t" style={{ borderColor: '#1e3a5f' }}>
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`flex items-center justify-between py-2.5 px-3 rounded text-sm font-medium tracking-wide uppercase transition ${
                  location.pathname === link.to ? 'text-gold-500 bg-white/5' : 'text-gray-300 hover:bg-white/5'
                }`}
              >
                <span>{link.label}</span>
              </Link>
            ))}
            {realUser?.role === 'admin' && (
              <label className="flex items-center justify-between mt-2 pt-3 border-t px-3 text-xs text-gray-400 uppercase tracking-wide" style={{ borderColor: '#1e3a5f' }}>
                View as
                <select
                  value={viewAs || 'admin'}
                  onChange={(e) => handleViewAs(e.target.value)}
                  className="text-xs rounded border px-1.5 py-1 uppercase font-bold focus:outline-none"
                  style={{ backgroundColor: '#0d1b2e', borderColor: viewAs ? '#c9a84c' : '#1e3a5f', color: viewAs ? '#c9a84c' : '#9ca3af' }}
                >
                  {VIEW_AS_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </label>
            )}
            <div className="flex items-center justify-between mt-2 pt-3 border-t px-3" style={{ borderColor: '#1e3a5f' }}>
              <span className="text-xs text-gray-400">
                {user?.name} &middot; <span className="uppercase font-bold text-gold-500">{user?.role}</span>
              </span>
              <button onClick={handleLogout} className="text-xs text-red-400 hover:text-red-300 uppercase tracking-wide transition">
                Sign out
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Impossible to forget you're in view-as: gold banner on every page until exited */}
      {viewAs && realUser?.role === 'admin' && (
        <div className="flex items-center justify-center gap-3 px-4 py-1.5 text-xs font-bold uppercase tracking-widest" style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}>
          Viewing as {VIEW_AS_ROLES.find((r) => r.value === viewAs)?.label || viewAs} — this is what they see
          <button
            onClick={() => handleViewAs('admin')}
            className="rounded px-2 py-0.5 font-black transition hover:opacity-80"
            style={{ backgroundColor: '#0d1b2e', color: '#c9a84c' }}
          >
            Exit
          </button>
        </div>
      )}
      {ticketAlert && (
        <div
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-xs font-semibold text-center"
          style={{ backgroundColor: ticketAlert.level === 'error' ? '#7f1d1d' : '#78350f', color: '#fee2e2' }}
          title={ticketAlert.detail || undefined}
        >
          <span>⚠ {ticketAlert.message}</span>
          {location.pathname !== '/tickets' && (
            <Link to="/tickets" className="underline font-bold hover:opacity-80">Open 811 Ticket Finder</Link>
          )}
        </div>
      )}
      <main className="flex-1 p-4 md:p-6">{children}</main>
      <footer className="text-center text-xs text-gray-500 py-4">
        &copy; {new Date().getFullYear()} BLVD LLC
      </footer>
      <ChatWidget />
    </div>
  );
}
